import { useState } from "react";
import { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { Error as ApiError } from "~/api/Api";

/**
 * Handles server-side validation errors from ApiError.
 *
 * Three cases:
 * 1. Field errors — field name matches a form input → setError(field) shows error under the input.
 * 2. "base" errors — not tied to any field → shown in <FormBaseError> above the form.
 * 3. Unmapped fields — server returns errors for fields not present in the form (e.g. reset_password_token)
 *    → pass them in `unmappedFields`, their errors appear in <FormBaseError> as "fieldName: message".
 */
export function useFormErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  unmappedFields: string[] = [],
) {
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const applyServerErrors = (error: ApiError) => {
    const nextBaseErrors: string[] = [];

    if (error.validations) {
      for (const [field, messages] of Object.entries(error.validations)) {
        if (field === "base") {
          nextBaseErrors.push(...messages);
        } else if (unmappedFields.includes(field)) {
          nextBaseErrors.push(`${field}: ${messages.join(", ")}`);
        } else {
          setError(field as Path<T>, { message: messages.join(", ") });
        }
      }
    }

    // Fallback: server returned a plain error without validations (e.g. "We couldn't find the record").
    // Show the top-level message in the base error block.
    if (nextBaseErrors.length === 0 && !error.validations) {
      nextBaseErrors.push(error.message);
    }

    setBaseErrors(nextBaseErrors);
  };

  const clearBaseErrors = () => setBaseErrors([]);

  return { baseErrors, applyServerErrors, clearBaseErrors };
}
